import { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, ModalDefault, Notice, Skeleton, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, formatDateTime } from "@components/ow";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Image = styled.img`
  display: block;
  max-width: 100%;
  max-height: 300px;
  margin: 0 auto;
  border-radius: 10px;
  object-fit: contain;
`;

// O texto como foi digitado, com as quebras de linha
const Message = styled.div`
  white-space: pre-line;
  overflow-wrap: anywhere;
`;

/**
 * O comunicado como o colaborador vê: imagem (quando há) e o texto inteiro. Usado
 * em /c/alerts e nos comunicados do Dashboard do prestador.
 * A imagem sai pelo GET /alerts/:id/image: URL assinada com S3 ou o arquivo,
 * mostrado por um objectURL que é revogado ao fechar.
 */
export default function AlertViewModal({ alert, onClose }) {
  const alertsService = useService("alerts");
  const [image, setImage] = useState({ loading: !!alert.image_url, src: null, failed: false });

  useEffect(() => {
    if (!alert.image_url) return undefined;
    let active = true;
    let objectUrl = null;

    alertsService.getImage(alert.id).then((result) => {
      if (!active) return;
      if (result?.url) {
        setImage({ loading: false, src: result.url, failed: false });
      } else if (result instanceof Blob) {
        objectUrl = URL.createObjectURL(result);
        setImage({ loading: false, src: objectUrl, failed: false });
      } else {
        setImage({ loading: false, src: null, failed: true });
      }
    });

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert.id, alert.image_url]);

  return (
    <ModalDefault
      $showModal
      $setShowModal={onClose}
      $size="medium"
      $title="Comunicado"
      $contentBackground="N0"
      $footer={<Button $text="Fechar" $variant="outline" $action={onClose} $noWidth />}
    >
      <Body>
        {image.loading ? <Skeleton $height={240} /> : null}
        {image.src ? (
          <Image
            src={image.src}
            alt="Imagem do comunicado"
            onError={() => setImage({ loading: false, src: null, failed: true })}
          />
        ) : null}
        {image.failed ? <Notice $variant="gray" $text="Não foi possível carregar a imagem." /> : null}
        <Message>
          <Text $text={escapeHtml(alert.text || "")} $variant="paragraph_small_medium" $color="N900" />
        </Message>
        <Text
          $text="Criado em %s"
          $replaces={[formatDateTime(alert.created_at)]}
          $variant="caption_medium"
          $color="N400"
          $colorEmphasis="N400"
        />
      </Body>
    </ModalDefault>
  );
}
